/**
 * StandKonumKarti.jsx — bir standın salon krokisindeki YERİ ("Konum"), personel sayfası ve firma portalı için ORTAK
 * (backlog #78, #242, #243, #244; firma portalına taşındı #439, 2026-10-16).
 *
 * Eskiden Stand Detay (personel) sayfasının içindeki `KonumTab`'dı; firma portalının stand sayfaları (Standlarım › Konum,
 * Ana Sayfa › stand) ise sahte bir önizleme (KrokiShape / AlanKrokiPlaceholder) gösteriyordu. İkisi artık aynı bileşeni ve
 * aynı gömme parametrelerini kullanır: fdsvector /embed/stand-plan, SALT OKUNUR. Üç görünüm (#242):
 *   "Alan"           standın çevresi: standın kutusu Sistem Ayarları'ndaki Alan kenar payı kadar büyütülür, içindeki her
 *                    şey kırpılarak gösterilir (around + margins); stand etiketli + vurgulu (selectedStandId)
 *   "Salon"          tüm salon, açılışta Salon Planı çerçevesine + Salon kenar payına sığdırılır (fit=hall + margins);
 *                    yalnızca bu stand etiketli + vurgulu, katman paneli yok
 *   "Yalnızca Stand" yalnızca bu standın şekli, Stand kenar payıyla çerçevelenmiş (standIds + margins)
 * Kenar payları: Sistem Ayarları › Plan Ayarları (#243, getKonumMargins; okuma her girişe açık); okunamazsa backend'in
 * varsayılanlarıyla aynı yedek değerler kullanılır. Hepsi krokideki şeklin data.standId'si bu standın id'si olduğunda eşleşir;
 * o bağ fdsvector'ün düzenleme modunda kurulur (personelin işi).
 * Önce kroki yoklanır: çapraz kaynak iframe'in içindeki 404 ekranını okuyamayız (bkz. krokiVectorService.resolveStandPlanDocument).
 *
 * `mod`:
 *   "personel" (varsayılan) — Stand Detay sayfasının eski davranışı birebir: kenar payı satırı, "krokide bağlayın" ipuçları,
 *                             tıklanan standın detayına gitme (onStandClick), kaydedince yeniden yükleme (onChanged).
 *   "firma"                 — firma portalı (#439): gömmenin geliştirici araçları (id deneme paneli, yeni sekmede aç) gizli,
 *                             başka standa gidilmez, personele yönelik ipuçları yok. Erişimi fdsvector kendisi sınırlar:
 *                             GET /portal/kroki-erisim (#406) bir firmaya yalnızca KENDİ standının salonunu (Katılımcı ya da
 *                             Dekoratör; Taşeron asla) salt okunur açar. Embed'in `viewer=` değeri yok: katılımcı / dekoratör
 *                             düz mode=view açar, fdsvector firma oturumunda her yazmayı zaten reddeder (refuseCompanyWrites).
 *
 * Alan görünümü salon planını (diğer katılımcıların konumlarını) kenar payı kadar gösterir; Salon görünümü tüm salonu. Diğer
 * katılımcıların ADLARI etiket olarak çizilmez: yalnızca bu stand etiketli (selectedStandId).
 */
import React, { useEffect, useState } from "react";
import { Alert, Button, ButtonGroup, Card, CardBody, CardHeader, Spinner } from "reactstrap";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import KrokiVectorEmbed from "./KrokiVectorEmbed";
import { resolveStandPlanDocument } from "../../services/krokiVectorService";
import { getKonumMargins } from "../../services/systemSettingsService";
import i18n from "../../i18n";

const KONUM_GORUNUMLERI = [
  { key: "alan", get label() { return i18n.t("moduller:standDetay.alan"); } },
  { key: "salon", get label() { return i18n.t("moduller:standDetay.salon"); } },
  { key: "stand", get label() { return i18n.t("moduller:standDetay.yalnizcaStand"); } },
];

// "sol 10 · üst 10 · sağ 10 · alt 10 m" — eşitse tek sayı.
function kenarPayiMetni(m) {
  if (!m) return "";
  const f = (v) => String(+Number(v).toFixed(2));
  const { left, top, right, bottom } = m;
  if (left === top && top === right && right === bottom) return i18n.t("moduller:standDetay.herYondeM", { f: f(left) });
  return i18n.t("moduller:standDetay.solUstSagAltM", { f: f(left), f2: f(top), f3: f(right), f4: f(bottom) });
}

/**
 * @param {object}   s             stand: { standId, standName, salonId, salonName, fuarId }
 * @param {string}   mod           "personel" | "firma"
 * @param {Function} onChanged     personel: fdsvector 'saved' olayında standı yeniden yükler (#180)
 * @param {Function} onStandClick  personel: başka bir standa tıklanınca (e: { standId }) o standın detayına gider (#180)
 */
export default function StandKonumKarti({ s, mod = "personel", onChanged, onStandClick }) {
  const { t } = useTranslation("moduller");
  const { t: tp } = useTranslation("portal");
  const firma = mod === "firma";
  const [gorunum, setGorunum] = useState("alan");
  // #242/#243: kenar payları Sistem Ayarları'ndan; bir kez okunur.
  const [kenarPaylari, setKenarPaylari] = useState(null);
  useEffect(() => {
    let ignore = false;
    getKonumMargins().then((m) => { if (!ignore) setKenarPaylari(m); });
    return () => { ignore = true; };
  }, []);
  // Hangi görünüm + stand için geldiyse onunla anahtarlanır — görünüm değişince eski sonuç düşer.
  const [isolateSonuc, setIsolateSonuc] = useState(null);
  const isolateKey = `${gorunum}|${s.standId}`;
  const eslesme = isolateSonuc?.key === isolateKey ? isolateSonuc.result : null;
  const bagliDegil = eslesme != null && (
    eslesme.matched === 0 || (eslesme.missingStandIds ?? []).map(String).includes(String(s.standId))
  );
  // Yoklama sonucu hangi fuar/salon çiftine aitse onunla anahtarlanır: çift değişince
  // eski sonuç kendiliğinden geçersiz sayılır — Effect içinde sıfırlamaya gerek yok.
  const probeKey = `${s.fuarId}|${s.salonId}`;
  const [probeSonuc, setProbeSonuc] = useState(null);
  const probe = probeSonuc?.key === probeKey ? probeSonuc.sonuc : null;

  useEffect(() => {
    if (!s.salonId) return undefined;
    let ignore = false;
    resolveStandPlanDocument({ fairId: s.fuarId, hallId: s.salonId })
      .then((sonuc) => { if (!ignore) setProbeSonuc({ key: probeKey, sonuc }); });
    return () => { ignore = true; };
  }, [probeKey, s.fuarId, s.salonId]);

  const kenarPayi = kenarPaylari?.[gorunum];
  const viewOptions = gorunum === "stand"
    ? { standIds: [s.standId], margins: kenarPayi }
    : gorunum === "alan"
      ? { around: [s.standId], margins: kenarPayi, selectedStandId: s.standId }
      : { layers: "none", selectedStandId: s.standId, fit: "hall", margins: kenarPayi };

  const planYok = probe && (probe.reason === "not-mapped" || probe.reason === "missing-id");

  let govde;
  if (!s.salonId) {
    govde = <div className="text-muted">{t("standDetay.standBirSalonaBagliDegil")}</div>;
  } else if (!probe || !kenarPaylari) {
    // Kenar payları gelmeden iframe açılmaz — yoksa önce payısız açılıp payla yeniden yüklenirdi.
    govde = (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <div className="text-muted small mt-2">{t("standDetay.krokiAraniyor")}</div>
      </div>
    );
  } else if (planYok && firma) {
    govde = (
      <Alert color="info" className="small mb-0">
        {tp("standKonum.planHazirDegil", { salon: s.salonName || "" })}
      </Alert>
    );
  } else if (planYok) {
    govde = (
      <Alert color="warning" className="d-flex align-items-start gap-2 mb-0">
        <AlertTriangle size={18} className="flex-shrink-0 mt-1" />
        <div>
          {probe.reason === "missing-id"
            ? <>{t("standDetay.standinFuarSalonBilgisiEksik")}<code>{t("standDetay.fuarId")}{String(s.fuarId ?? "—")}</code> · <code>{t("standDetay.salonId")}{String(s.salonId ?? "—")}</code>).</>
            : <>{s.salonName || t("standDetay.buSalonun")}{" "}{t("standDetay.buFuarIcinKrokisiYok")}<code>{t("standDetay.fuarId")}{String(s.fuarId)}</code> · <code>{t("standDetay.salonId")}{String(s.salonId)}</code>).</>}
        </div>
      </Alert>
    );
  } else {
    // ok, ya da yoklama başarısız (ör. kroki oturumu henüz kurulmadı — iframe kendi
    // oturumunu kurar): krokiyi yine de aç. Personel başarısızlığı üstte görür, firma görmez (teknik ileti).
    govde = (
      <>
        {!firma && !probe.ok && (
          <Alert color="warning" className="py-2 small">
            {t("standDetay.krokiOncedenDogrulanamadiGomuluUygulama", { message: probe.message })}
          </Alert>
        )}
        {!firma && (
          <div className="text-muted mb-2" style={{ fontSize: 11 }}>
            {t("standDetay.kenarPayi", { kenarPayiMetni: kenarPayiMetni(kenarPayi) })}
            {kenarPaylari.fromSettings
              ? t("standDetay.sistemAyarlariPlanAyarlari")
              : t("standDetay.varsayilanSistemAyarlariOkunamadi")}
          </div>
        )}
        <KrokiVectorEmbed
          fairId={s.fuarId}
          hallId={s.salonId}
          hallName={s.salonName}
          height={600}
          allowEdit={false}
          gelistiriciAraclari={!firma}
          viewOptions={viewOptions}
          onIsolateResult={(result) => setIsolateSonuc({ key: isolateKey, result })}
          onStandClick={firma ? undefined : onStandClick}
          onSaved={firma ? undefined : () => onChanged?.()}
        />
        {firma ? (
          <div className="text-muted mt-2" style={{ fontSize: 11 }}>
            {bagliDegil ? tp("standKonum.standPlandaIsaretliDegil") : tp("standKonum.gorunmuyorsaSakaIleIletisim")}
          </div>
        ) : bagliDegil ? (
          <Alert color="warning" className="py-2 small mt-2 mb-0">
            <strong>{s.standName}</strong>{" "}{t("standDetay.buPlandaBagliDegilKrokideki")}{" "}<strong>{t("standDetay.stand")}</strong>
            {t("standDetay.bolumundenBuStandiBaglayin")}
          </Alert>
        ) : (
          <div className="text-muted mt-2" style={{ fontSize: 11 }}>
            {t("standDetay.standKrokideGorunmuyorsaKrokiUygulamasinda")}{" "}<strong>{t("standDetay.stand")}</strong>{" "}{t("standDetay.bolumunden")}
            <strong> {s.standName}</strong>{" "}{t("standDetay.standiniBaglayin")}
          </div>
        )}
      </>
    );
  }

  return (
    <Card>
      <CardHeader className="py-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
        <span className="fw-semibold">{s.salonName || t("standDetay.konum")}</span>
        {s.salonId && probe && !planYok && (
          <ButtonGroup size="sm">
            {KONUM_GORUNUMLERI.map((g) => (
              <Button
                key={g.key}
                color="primary"
                outline={gorunum !== g.key}
                onClick={() => setGorunum(g.key)}
              >
                {g.label}
              </Button>
            ))}
          </ButtonGroup>
        )}
      </CardHeader>
      <CardBody>{govde}</CardBody>
    </Card>
  );
}
